import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandGroup,
  CommandInput,
  CommandList,
  CommandSpinner,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function MethodCommandList({
  placeholder = 'Contact Method',
  selected = [],
  onSelect,
  multiselect,
}: BaseCommandListProps<false, string>) {
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Methods)

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return data
    const normalizedQuery = query.toLowerCase()
    return data?.filter((method) => method.name.toLowerCase().includes(normalizedQuery))
  }, [data, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      onSelect(newSelection)
    },
    [onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        onSelect([id])
      }
    },
    [multiselect, selected, onSelect],
  )

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      <CommandInput
        placeholder={placeholder}
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="py-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={multiselect}
          />
          {visibleItems?.map((method) => (
            <CommandCheckItem
              key={method.id}
              value={method.id}
              keywords={[method.name]}
              className="group mx-1 flex gap-3"
              selected={selected.includes(method.id)}
              onSelect={() => handleToggleItem(method.id)}
              multiselect={multiselect}
            >
              {method.name}
            </CommandCheckItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
